/**
 * Copy of the bid simulator: the standalone page, the accuracy page, the
 * what-if panel in AMS View's Targets drill and the forecast inside the
 * change-bid dialog. Wired into `en.ts` as `bidSimulator`. VentaHub's English,
 * verbatim, from `components/features/bid-simulator/*`,
 * `pages/ams_view/BidSimulatorPage.tsx`, `pages/ams_view/bidSimulator/*`,
 * `pages/ams_view/BidForecastAccuracyPage.tsx` and
 * `pages/ams_view/components/BidWhatIfPanel.tsx` (cited per block).
 *
 * The flag, reason, provenance and size-class maps are keyed by the codes the
 * backend emits. A code with no copy is shown, not swallowed (`copy.ts`).
 */
export const bidSimulator = {
  // copy.ts:31-45 — FLAG_TEXT
  flag: {
    settings_unknown:
      'Campaign placement and bidding-strategy settings are not collected yet, so cost per click has no ceiling applied.',
    extrapolation:
      'This bid is beyond any change measured for this account — the response is held flat, not extrapolated.',
    no_recent_traffic:
      'This target had no clicks in the last fortnight, so there is no rate to forecast from.',
    no_recent_clicks:
      'No clicks in the last four weeks, so there is no cost per click to move from.',
    no_settled_orders: 'No settled orders yet, so orders, sales and ACOS cannot be estimated.',
    history_short: 'Less than a full fortnight of report data — treat the rate as provisional.',
    adgroup_dark_risk:
      'This ad group often shows nothing at all; the forecast is discounted for that.',
    orders_mostly_noise:
      'Fewer than a few orders expected: treat orders and ACOS as a coin toss, not a number.',
    no_parameters: 'No parameters are installed for this store yet.',
    bid_unknown: 'No bid is recorded for this target, so there is nothing to move from.',
    // copy.ts:127-129
    unknown: 'Flagged "{{code}}" — no explanation is written for this code yet.',
  },
  // copy.ts:58-73 — NO_ADVICE_TEXT
  noAdvice: {
    inside_tolerance: 'Already within 5 % of where the ACOS goal puts it — not worth changing.',
    cool_down:
      'The bid was moved across this recommendation in the last week. Leaving it to settle.',
    target_not_enabled: 'This target is not running.',
    no_target_acos: 'This ad group has no ACOS goal set, so there is no bid to aim at.',
    thin_data: 'Too few clicks for the ACOS behind the advice to mean anything.',
    no_recent_traffic: 'No clicks in the last fortnight.',
    no_recent_clicks:
      'No clicks in the last four weeks, so there is no cost per click to move from.',
    no_settled_orders: 'No settled orders, so a click cannot be valued yet.',
    history_short: 'Not enough report history yet.',
    adgroup_dark_risk:
      'This ad group is often not serving at all; a bid change would not be measurable.',
    extrapolation: 'The bid the goal implies is outside anything measured for this account.',
    bid_unknown: 'This target has no bid recorded.',
    // copy.ts:132-135
    unknown: 'Reason given: "{{reason}}".',
  },
  // copy.ts:85-109 — PARAMETER_PROVENANCE
  provenance: {
    store: { label: 'fitted here', tip: "Fitted on this store's own recent weeks." },
    country: {
      label: 'fitted for this marketplace',
      tip: 'Fitted across this marketplace, not this store specifically.',
    },
    seed: {
      label: 'research, not this account',
      tip: 'From research on other accounts and an earlier period — not yet fitted to this store.',
    },
    none: {
      label: 'not measured',
      tip: 'No fitted parameter is installed at any level; a built-in default was used.',
    },
  },
  // copy.ts:112-116 — PARAMETER_PIECE_TIP
  piece: {
    baseline: 'The correction applied to the recent click rate before anything else.',
    band: 'The measured spread one keyword-week lands in — the range shown beside every figure.',
    response: 'How much traffic a bid change buys, measured across keywords that moved.',
  },
  // copy.ts:119-128 — SIZE_CLASS_LABEL
  sizeClass: {
    all: 'All keywords',
    Z: 'No clicks',
    T0: 'Under 3 clicks a week',
    T1: '3–10 clicks a week',
    T2: '10–30 clicks a week',
    T3: '30–100 clicks a week',
    T4: '100–300 clicks a week',
    T5: '300+ clicks a week',
  },
  // format.ts, ForecastDeltas.tsx:28-34
  format: {
    noChange: 'no change',
    pp: '{{value}}pp',
    range: '{{lo}}–{{hi}}',
    times: 'x{{value}}',
  },
  // ForecastAtoms.tsx
  atoms: {
    rangeNotMeasured: 'range not measured',
    range: 'range {{range}}',
    chip: '{{piece}}: {{level}}',
    provenance: 'Where the parameters came from',
    flags: 'Caveats',
  },
  // ForecastDeltas.tsx:72-174
  deltas: {
    clicks: 'Clicks',
    clicksTip:
      'Expected is the average across many keywords in this situation. Typical is what the middle keyword does — lower, because a few big winners pull the average up.',
    vsToday: '{{change}} vs {{now}} today',
    onToday: 'x{{value}} on today',
    middle: '{{value}} for the middle keyword',
    actual: '{{clicks}} actual in the last {{days}} days',
    spend: 'Spend',
    noSpendCompare: 'no comparison — today’s bid is not known',
    noCpc: 'no cost per click measured, so spend cannot be estimated',
    acos: 'ACOS',
    acosTip:
      'ACOS follows cost per click: neither the conversion rate nor the selling price moves with the bid.',
    noAcosCompare: 'no comparison — today’s ACOS is not known',
    noOrders: 'no settled orders, so a click cannot be valued',
    cpc: 'Cost per click',
    cpcTip:
      'Cost per click rises more slowly than the bid: raising a bid mostly buys auctions that were already close.',
    cpcUnknown: 'today’s cost per click is not known',
    orders: 'Orders',
    ordersTip:
      'Order counts on one keyword are mostly luck. The chance of no order at all is the honest version of this figure.',
    chanceNone: '{{pct}} chance of none at all',
    notEstimated: 'not estimated',
    impressions: 'Impressions',
  },
  // RecommendationCallout.tsx:46-79
  advice: {
    suggested: 'Suggested bid {{bid}}.',
    suggestedStep:
      'Suggested bid {{bid}} — one step; the ACOS goal implies further, over more moves.',
    suggestedMin: 'Suggested bid {{bid}} — the marketplace minimum.',
    none: 'No bid change suggested.',
    noneBecause: 'No bid change suggested — {{reason}}',
    tryIt: 'Try it',
    useThis: 'Use this bid',
    // TargetCard.tsx:263, BidWhatIfPanel.tsx:180
    pageFootnote: 'Nothing on this page changes anything on Amazon.',
    panelFootnote: 'Nothing is changed on Amazon by this panel.',
  },
  // BidCurveChart.tsx
  chart: {
    title: 'Bid curve',
    clicks: 'Clicks',
    spend: 'Spend',
    acos: 'ACOS',
    expected: 'expected',
    typical: 'typical',
    band: '90% range',
    bandNotMeasured: 'range not measured',
    noCpc: 'no cost per click measured',
    noCpcNote: 'This target has had no clicks recently, so there is no cost per click to spend at.',
    noRange: 'no range measured',
    noOrders: 'no settled orders',
    acosNote:
      'ACOS follows cost per click alone: neither the conversion rate nor the price moves with the bid.',
    acosNoOrdersNote:
      'Without settled orders a click cannot be valued, so there is no ACOS to forecast.',
    today: 'today',
    suggested: 'suggested',
    asked: 'asked',
    bidLabel: 'Bid {{bid}}',
    plot: '{{title}} against the bid',
    collapsed_one:
      '{{count}} of the sampled bids rounded onto another — at this bid the ladder is coarser than eleven steps.',
    collapsed_other:
      '{{count}} of the sampled bids rounded onto another — at this bid the ladder is coarser than eleven steps.',
    showNumbers: 'Show the numbers',
    hideNumbers: 'Hide the numbers',
    columns: {
      bid: 'Bid',
      clicks: 'Clicks',
      clicksRange: 'Clicks range',
      spend: 'Spend',
      spendRange: 'Spend range',
      acos: 'ACOS',
    },
    todayRow: '{{bid}} · today',
    notMeasured: 'not measured',
  },
  // ChangeBidForecast.tsx:96-145
  modalForecast: {
    heading: 'Forecast — this does not change what will be applied',
    working: 'Working out what this bid would do…',
    unavailable:
      'Not enough history to forecast this target, so there is no estimate of what this bid will do.',
    at: 'At {{bid}} over the next {{days}} days',
    atRefreshing: 'At {{bid}} over the next {{days}} days — a newer answer is still loading',
  },
  // useBidForecast.ts:139-144, BidWhatIfPanel.tsx:118-124
  errors: {
    unanswerable: 'That question cannot be answered for this keyword yet.',
    forbidden: 'The bid simulator is internal-only until its accuracy has been published.',
    failed: 'Could not load the forecast.',
  },
  // TargetCard.tsx:58-68, BidWhatIfPanel.tsx:63-67
  mode: {
    bid: 'Bid',
    cpc: 'Cost per click',
    acos: 'ACOS goal',
  },
  field: {
    bid: 'Bid',
    cpc: 'Cost per click',
    acos: 'Target ACOS (%)',
  },
  // BidWhatIfPanel.tsx
  panel: {
    label: 'Bid what-if',
    askBy: 'Ask by',
    value: 'What-if value',
    over: 'Over',
    days_one: '{{count}} day',
    days_other: '{{count}} days',
    loading: 'Loading forecast',
    unavailable: 'Not enough history to forecast this target yet.',
    bid: 'Bid',
    nowDelta: 'now {{bid}} ({{delta}})',
    expectedTypical: '{{expected}} expected, {{typical}} typical',
    expected: '{{expected}} expected',
    cpcNow: 'now {{cpc}}',
    acosNow: 'now {{acos}}',
    chanceNone: '{{pct}} chance of none',
    dark: 'The whole ad group showed nothing on about {{days}} of the last {{window}} days. Those days are already inside the rate the figures above are built from, so they are not subtracted again.',
    showWorking: 'Show working',
    hideWorking: 'Hide working',
    accuracyLink: 'How accurate has this been?',
    simulatorLink: 'Open the full simulator',
    asOf: 'Based on report data complete to {{day}}.',
  },
  // BidSimulatorPage.tsx
  page: {
    title: 'Bid simulator',
    description:
      'What a bid change is forecast to do — with the range it could land in, and where every number came from',
    horizon: 'Forecast over',
    window: 'Keyword list window',
    windowDays: 'Last {{count}} days',
    loadingTrackRecord: 'Loading track record',
    forbidden:
      'The bid simulator is internal-only until its accuracy has been published. Keyword search below still works; no forecast will be shown.',
    noStores:
      'No Amazon store with the Ads API enabled is available on this client. The simulator is fitted on Amazon advertising reports and has nothing to answer for other marketplaces.',
    accuracyFailed:
      'Could not load the track record for this store, so the forecasts below carry no measure of how often they have been right. That is a failed request, not a verdict on the model.',
    neverRun:
      'The backtest has never been run for this store, so nothing here has a measured track record yet. The forecasts below are still produced — the model falls back to research parameters — they simply have nothing behind them saying how often they have been right. It is not on a schedule: it runs when the bid simulator refit job is dispatched, which covers every store with ad groups.',
    frozenAt: 'Parameters frozen at {{day}}',
    knowable: 'Bid changes knowable for {{pct}} of scored weeks',
    knowableUnrecorded: 'Bid-change coverage not recorded for these rows',
    measured:
      'Measured: the {{components}}. The dark-day discount the live forecast applies is not among them.',
    and: ' and ',
    scorecardLink: 'The full scorecard for this store',
    way: 'How to find a keyword',
    browse: 'Browse ad groups',
    search: 'Search keywords',
    compared_one: '{{count}} keyword',
    compared_other: '{{count}} keywords, side by side',
    limit: '{{count}} is the limit — remove one to add another.',
    pickStore: 'Pick a store',
    pickStoreHint: 'The simulator answers for one Amazon store at a time.',
  },
  // TargetBrowser.tsx
  browser: {
    filter: 'Filter ad groups',
    filterPlaceholder: 'ad group or campaign name',
    loading: 'Loading ad groups',
    allAdGroups: '← All ad groups',
    failed: 'Could not load this store’s ad groups.',
    none: 'This store has no ad groups at all — not just none with data in this window.',
    count_one: '{{count}} ad group',
    count_other: '{{count}} ad groups',
    matching: '{{matched}} of {{total}} ad groups match',
    showingFirst: ' — showing the first {{count}}; filter to reach the rest.',
    fromMaster:
      'Ad groups are listed from the master record, so one with no traffic in this window still appears.',
    columns: {
      adGroup: 'Ad group',
      type: 'Type',
      targets: 'Targets',
      spend: 'Spend',
      acos: 'ACOS',
      goal: 'Goal',
      target: 'Target',
      match: 'Match',
      bid: 'Bid',
      clicks: 'Clicks ({{days}}d)',
      acosVsGoal: 'ACOS vs goal',
    },
    campaignUnknown: 'campaign unknown',
    noData: 'no data',
    noRowsLast: 'No rows in this window. Last data {{day}}.',
    noRows: 'No rows in this window.',
    targets: 'Targets',
    targetsFor: 'Targets of {{name}}',
    noGoal: 'no ACOS goal set',
    goal: 'goal {{pct}}',
    targetsFailed: 'Could not load this ad group’s targets.',
    loadingTargets: 'Loading targets…',
    noTargets: 'This ad group has no targets.',
    ranking_one:
      '{{count}} target, worst overspend first — ranked by the spend above this ad group’s ACOS goal, not by spend alone. A target that spent and sold nothing counts as all of it above goal, so it ranks among the worst. One with sales but no goal set on the ad group cannot be ranked and sits last. Clicks and spend here cover the last {{days}} days; a forecast covers the next 7 or 14, and is built only from the last 14. A keyword that was busy early in this window and has since gone quiet will therefore show a large figure here and a small forecast — both correct. Each forecast card prints the actual over its own span beside it.',
    ranking_other:
      '{{count}} targets, worst overspend first — ranked by the spend above this ad group’s ACOS goal, not by spend alone. A target that spent and sold nothing counts as all of it above goal, so it ranks among the worst. One with sales but no goal set on the ad group cannot be ranked and sits last. Clicks and spend here cover the last {{days}} days; a forecast covers the next 7 or 14, and is built only from the last 14. A keyword that was busy early in this window and has since gone quiet will therefore show a large figure here and a small forecast — both correct. Each forecast card prints the actual over its own span beside it.',
    noDataWindow: 'no data in window',
    nothingSold: 'nothing sold',
    allAbove: 'all {{spend}} above goal',
    noGoalOnAdGroup: 'no goal set on the ad group',
    vsGoal: '{{gap}} vs {{goal}} goal',
    simulate: 'Simulate',
    simulateFor: 'Simulate {{name}}',
    noTargetId: 'This row has no target id, so the simulator cannot address it',
    already: 'Already in the comparison',
    full: 'Remove one from the comparison first',
  },
  // TargetFinder.tsx
  finder: {
    search: 'Search keywords',
    placeholder: 'part of the keyword text',
    submit: 'Search',
    searching: 'Searching keywords',
    hint: 'Matches the keyword text only — not a target id, and not an ad group or campaign name. The figures in this table are for the window above; the forecast is not, and always uses the simulator’s own recent-rate window.',
    failed: 'Could not search this store’s keywords.',
    noMatch: 'No keyword in this store matched “{{term}}” in this window.',
    noData: 'No keywords with data in this window for this store.',
    showing: 'Showing {{shown}} of {{total}} matching keywords, highest spend first',
    truncated:
      ' — and the search itself hit the server’s row cap, so the tail is not in this list at all.',
    narrow: '. Narrow the search to reach the rest.',
    columns: {
      keyword: 'Keyword',
      match: 'Match',
      bid: 'Bid',
      clicks: 'Clicks',
      spend: 'Spend',
      acos: 'ACOS',
    },
    inAdGroups: 'in {{count}} ad groups',
    chooseAdGroup: 'Choose ad group',
    chooseAdGroupFor: 'Choose ad group for {{name}}',
    hideAdGroups: 'Hide ad groups',
    hideAdGroupsFor: 'Hide ad groups for {{name}}',
    instancesFailed: 'Could not list the ad groups carrying this keyword.',
    findingInstances: 'Finding the ad groups this keyword is in…',
    noInstances: 'No live instances of this keyword were found in the selected window.',
    instanceBid: 'bid {{bid}}',
    inComparison: 'In comparison',
    simulateIn: 'Simulate {{name}} in {{adGroup}}',
  },
  // TargetCard.tsx
  card: {
    remove: 'Remove',
    removeFor: 'Remove {{name}}',
    bidNow: 'bid now {{bid}}',
    noBid: 'no bid recorded',
    dataTo: 'report data to {{day}}',
    askByFor: 'Ask by for {{name}}',
    valueFor: 'What-if value for {{name}}',
    loading: 'Loading forecast',
    cpcPrompt:
      'Type the cost per click you want and press Enter — the answer is the bid that delivers it.',
    acosPrompt:
      'Type the ACOS you are aiming at and press Enter — the answer is the bid that delivers it.',
    unavailable: 'Not enough history to forecast this keyword yet.',
    notFound: 'The simulator has no record of this keyword in this store.',
    cpcNeeds: 'A cost per click of {{cpc}} needs a bid of {{bid}}',
    acosNeeds: 'An ACOS goal of {{acos}}% needs a bid of {{bid}}',
    onToday: ' — {{change}} on today’s {{now}}.',
    period: '.',
    atBid: 'At a bid of {{bid}} over the next {{days}} days',
    atSuggested:
      'At a bid of {{bid}} — the suggested bid, not today’s — over the next {{days}} days',
    stillLoading: ' — a newer answer is still loading',
    dark: 'The whole ad group showed nothing on about {{days}} of the last {{window}} days. Those days are already inside the rate the figures above are built from, so they are not subtracted a second time.',
    showWorking: 'Show working',
    hideWorking: 'Hide working',
    adviceSteps: 'How the suggestion was reached:',
    ladder: 'The trade-off across the bid ladder',
    ladderHint:
      'Half today’s bid to half as much again — the range the response was measured over. Beyond it the model holds the response flat rather than extrapolating.',
    ladderFailed: 'The bid ladder could not be loaded for this keyword.',
  },
  // CompareTable.tsx
  compare: {
    label: 'The keywords side by side',
    columns: {
      keyword: 'Keyword',
      size: 'Size',
      bid: 'Bid',
      clicks: 'Clicks',
      spend: 'Spend',
      acos: 'ACOS',
      parameters: 'Parameters',
      trackRecord: 'Track record',
    },
    now: 'now {{value}}',
    notEstimated: 'not estimated',
    noOrders: 'no settled orders',
    beats: 'beats naive by {{pp}}pp',
    behind: 'worse than naive by {{pp}}pp',
    level: 'level with naive',
    unmeasured: 'not measured',
    pending:
      '{{count}} of the keywords above have no forecast yet — either nothing has been asked for them, or there is not enough history.',
  },
  // accuracyForSize.ts
  verdict: {
    neverRun:
      'The backtest has not run for this store yet, so there is nothing measuring how well this forecast has done.',
    noSizeClass: 'This keyword has no size class, so no measured track record applies to it.',
    unmeasured:
      'Accuracy has not been measured for keywords this size ({{size}}), so this forecast has no track record behind it.',
    ceiling: ' The best anything could do on these weeks is {{pct}}.',
    bidChange: 'weeks after a bid change',
    noChange: 'weeks with no bid change',
    behind:
      'On {{size}} this model has scored {{pp}} points worse than simply carrying the recent rate forward, over {{n}} {{population}}. Treat the figures below as a direction, not a number.',
    beats:
      'On {{size}} this model has scored {{pp}} points better than carrying the recent rate forward, over {{n}} {{population}}.{{ceiling}}',
    level:
      'On {{size}} this model has scored about the same as carrying the recent rate forward, over {{n}} {{population}}. It is not adding much here.',
  },
  // BidForecastAccuracyPage.tsx
  accuracy: {
    title: 'Bid forecast accuracy',
    description: "Measured on weeks the model's parameters had never seen",
    horizon: 'Horizon',
    loading: 'Loading accuracy',
    failed: 'Could not load the accuracy scorecard.',
    notMeasured:
      'Not measured yet for this store. The backtest fits the model on older weeks and scores it on newer ones; until it has run there is nothing here worth showing. It is not on a schedule — it runs when the bid simulator refit job is dispatched, which covers every store with ad groups.',
    explainer:
      'Accuracy is 1 − WAPE: total error over total actual, so a keyword with 400 clicks counts for more than one with 2. It is shown beside the forecast it replaces (carrying the rate forward with no correction) and the ceiling — the best any model could do, because clicks are random even when the average is right.',
    frozenAt: 'Parameters frozen at {{day}}',
    known: 'Bid changes known for {{pct}} of weeks',
    knowableOnly:
      'Bid changes knowable for only {{pct}} of weeks — "no change" is mostly an assumption',
    measures: 'These figures measure the {{components}}',
    measuresResponse: ' — a week whose bid moved is forecast with the bid it actually had.',
    measuresEnd: '.',
    notDark:
      ' They do not measure the dark-day discount the live forecast also applies, so a served expectation is a little below a scored one.',
    rowSet: {
      no_change: 'Weeks with no bid change',
      bid_change: 'Weeks after a bid change',
    },
    columns: {
      size: 'Keyword size',
      metric: 'Metric',
      weeks: 'Weeks',
      model: 'This model',
      naive: 'Naive',
      ceiling: 'Ceiling',
      gap: 'Gap closed',
      bias: 'Bias',
      parameters: 'Parameters',
    },
    notScorable: 'not scorable',
    gapTip: 'Naive {{naive}} · this model {{model}} · ceiling {{ceiling}}',
    ofAvailable: '{{pct}} of what was available',
    behindNaive: 'behind the naive forecast',
    biasTip:
      'Total forecast ÷ total actual. Outside 0.85–1.15 the model is buying its score by running low.',
  },
} as const
